import React, { useState } from "react";
import { Star, Phone, Instagram, MapPin, Clock, Sparkles } from "lucide-react";
import { useDemoActions, useDemoSEO, DemoLangSwitch, DemoFooterCTA, StickyActions, buildRestaurantSchema, ReserveButton } from "./demoShared";
import { COMPANY } from "../../config/company";

const NAME = "Sultan Ocakbaşı";
const TAG = { de: "Modernes türkisches Grill-Restaurant", en: "Modern Turkish Grill House", tr: "Modern Türk Mangal Evi" };

const CATEGORIES = [
  { id: "starters", label: { de: "Vorspeisen", en: "Starters", tr: "Mezeler" } },
  { id: "grills",   label: { de: "Vom Grill",   en: "From the Grill", tr: "Mangaldan" } },
  { id: "pide",     label: { de: "Pide & Lahmacun", en: "Pide & Lahmacun", tr: "Pide & Lahmacun" } },
  { id: "sweets",   label: { de: "Süßes",       en: "Desserts", tr: "Tatlılar" } },
  { id: "drinks",   label: { de: "Getränke",    en: "Drinks", tr: "İçecekler" } },
];

const ITEMS = [
  { cat: "starters", img: "https://images.unsplash.com/photo-1530469912745-a215c6b256ea?w=900&q=80&auto=format&fit=crop", name: { de: "Mezeteller Royal", en: "Royal Mezze Platter", tr: "Saray Meze Tabağı" }, desc: { de: "Hummus, Babaganush, Cacık, Acılı Ezme, Atom — handgemacht.", en: "Hummus, babaganush, cacik, spicy ezme, atom — handmade.", tr: "Humus, babaganuş, cacık, acılı ezme, atom — el yapımı." }, price: "14,90 €", badge: { de: "Beliebt", en: "Popular", tr: "Popüler" } },
  { cat: "starters", img: "https://images.unsplash.com/photo-1601001815853-3835274403b3?w=900&q=80&auto=format&fit=crop", name: { de: "Sucuk vom Grill", en: "Grilled Sujuk", tr: "Izgara Sucuk" }, desc: { de: "Hausgemachte würzige Rinderwurst auf Holzkohle.", en: "House-made spicy beef sausage over charcoal.", tr: "Ev yapımı baharatlı dana sucuk, kömür ateşinde." }, price: "8,90 €" },
  { cat: "grills",   img: "https://images.unsplash.com/photo-1633237308525-cd587cf71926?w=900&q=80&auto=format&fit=crop", name: { de: "Adana Kebap", en: "Adana Kebap", tr: "Adana Kebap" }, desc: { de: "Würzig, mit Bulgur-Pilav und gerösteten Paprika.", en: "Spicy minced lamb with bulgur pilaf and roasted peppers.", tr: "Acılı kıyma, bulgur pilavı ve közlenmiş biber ile." }, price: "19,90 €", badge: { de: "Chef's Pick", en: "Chef's Pick", tr: "Şef'in Seçimi" } },
  { cat: "grills",   img: "https://images.unsplash.com/photo-1599487488170-d11ec9c172f0?w=900&q=80&auto=format&fit=crop", name: { de: "Lammspieß", en: "Lamb Shish", tr: "Kuzu Şiş" }, desc: { de: "Auf Holzkohle gegrilltes mariniertes Lammfilet.", en: "Marinated lamb fillet charcoal-grilled.", tr: "Mangalda marine kuzu file." }, price: "23,90 €" },
  { cat: "grills",   img: "https://images.unsplash.com/photo-1574484284002-952d92456975?w=900&q=80&auto=format&fit=crop", name: { de: "Iskender Tabağı", en: "Iskender Plate", tr: "İskender" }, desc: { de: "Döner auf Pide, Tomatensauce, geschmolzene Butter, Joghurt.", en: "Döner on pide, tomato sauce, melted butter, yogurt.", tr: "Pide üzerine döner, domates sosu, eritilmiş tereyağı, yoğurt." }, price: "18,90 €" },
  { cat: "pide",     img: "https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=900&q=80&auto=format&fit=crop", name: { de: "Pide mit Sucuk & Käse", en: "Sujuk & Cheese Pide", tr: "Sucuklu Kaşarlı Pide" }, desc: { de: "Steinofen, knusprig, mit Eigelb verfeinert.", en: "Stone oven, crisp, finished with egg yolk.", tr: "Taş fırın, çıtır, yumurta sarısı ile." }, price: "13,90 €" },
  { cat: "pide",     img: "https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=900&q=80&auto=format&fit=crop", name: { de: "Lahmacun", en: "Lahmacun", tr: "Lahmacun" }, desc: { de: "Dünn, würzig, mit frischer Petersilie und Zitrone.", en: "Thin, spicy, with fresh parsley and lemon.", tr: "İnce, baharatlı, taze maydanoz ve limon ile." }, price: "5,90 €" },
  { cat: "sweets",   img: "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=900&q=80&auto=format&fit=crop", name: { de: "Baklava (5 Stk.)", en: "Baklava (5 pcs)", tr: "Baklava (5 Adet)" }, desc: { de: "Pistazien, butterzart, leicht warm serviert.", en: "Pistachio, buttery, served warm.", tr: "Antep fıstıklı, tereyağlı, sıcak servis." }, price: "9,90 €", badge: { de: "Hausspezialität", en: "House Specialty", tr: "Ev Spesiyali" } },
  { cat: "sweets",   img: "https://images.unsplash.com/photo-1542444459-db63c9b3b0c0?w=900&q=80&auto=format&fit=crop", name: { de: "Künefe", en: "Künefe", tr: "Künefe" }, desc: { de: "Geschmolzener Käse in Engelshaar mit Sirup.", en: "Melted cheese in shredded pastry with syrup.", tr: "Tel kadayıf içinde eriyik peynir, şerbet ile." }, price: "8,50 €" },
  { cat: "drinks",   img: "https://images.unsplash.com/photo-1525480122447-64809d765a02?w=900&q=80&auto=format&fit=crop", name: { de: "Türkischer Mokka", en: "Turkish Mocha", tr: "Türk Kahvesi" }, desc: { de: "Doppelt geröstet, mit Lokum.", en: "Double-roasted, with Turkish delight.", tr: "Çift kavurmalı, lokumla." }, price: "3,90 €" },
  { cat: "drinks",   img: "https://images.unsplash.com/photo-1597318236861-1c25fee14bf2?w=900&q=80&auto=format&fit=crop", name: { de: "Ayran (frisch geschlagen)", en: "Ayran (fresh-whipped)", tr: "Ayran (köpüklü)" }, desc: { de: "Salzig-cremig, ideal zum Kebap.", en: "Salty-creamy, perfect with kebap.", tr: "Tuzlu-kremalı, kebapla mükemmel." }, price: "3,50 €" },
];

const COPY = {
  hero: {
    de: "Authentisches Feuer. Moderne Eleganz.",
    en: "Authentic fire. Modern elegance.",
    tr: "Otantik ateş. Modern zarafet.",
  },
  sub: {
    de: "Köstlichkeiten vom Holzkohlegrill, hausgemachte Mezze und himmlisches Baklava — direkt aus dem Herzen Anatoliens.",
    en: "Charcoal-grilled delights, house-made mezze and heavenly baklava — straight from the heart of Anatolia.",
    tr: "Kömürde mangal lezzetleri, ev yapımı mezeler ve cennet gibi baklavalar — Anadolu'nun kalbinden.",
  },
  reservation: { de: "Tisch reservieren", en: "Reserve a table", tr: "Masa rezervasyonu" },
  insta: { de: "Folge uns auf Instagram", en: "Follow us on Instagram", tr: "Bizi Instagram'da takip et" },
  open: { de: "Heute geöffnet bis 23:00", en: "Open today until 11pm", tr: "Bugün 23:00'a kadar açık" },
};

export default function DemoTurkishRestaurant() {
  const [lang, setLang] = useState("de");
  const [activeCat, setActiveCat] = useState("starters");
  const tx = (k) => k[lang] || k.de;
  const { callWaiter, requestBill } = useDemoActions(NAME, lang);

  useDemoSEO({
    lang,
    title: `${NAME} – ${tx(TAG)} | Demo · qrmenu-pro.com`,
    description: tx({
      de: `${NAME} Demo-Menü — sehe, wie ein elegantes QR-Menü für ein türkisches Restaurant aussieht. Adana, Iskender, Pide & Baklava — alles mit Bildern, Preisen und Live-Service.`,
      en: `${NAME} demo menu — see how a stunning QR menu for a Turkish restaurant looks. Adana, Iskender, Pide & Baklava — with photos, prices and live service.`,
      tr: `${NAME} demo menü — bir Türk restoranı için şık QR menü nasıl olur, gör. Adana, İskender, Pide & Baklava — fotoğraflar, fiyatlar ve canlı servis ile.`,
    }),
    schema: buildRestaurantSchema({
      url: "https://qrmenu-pro.com/demo-turkish-restaurant",
      name: NAME,
      image: ITEMS[2].img,
      priceRange: "€€",
      cuisine: "Turkish",
      sameAs: [COMPANY.instagramUrl],
    }),
  });

  const items = ITEMS.filter((i) => i.cat === activeCat);

  return (
    <div className="min-h-screen bg-[#0a0807] text-amber-50 selection:bg-amber-500/40" data-testid="demo-turkish-page">
      {/* HERO */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559847844-5315695dadae?w=1800&q=80&auto=format&fit=crop"
            alt="Charcoal grill atmosphere"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0807]/60 via-[#0a0807]/85 to-[#0a0807]"></div>
        </div>
        <div className="relative container mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="flex items-start justify-between gap-4">
            {/* Logo / wordmark */}
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-amber-400/70 font-semibold">Anatolia · Recklinghausen</div>
              <h1
                className="mt-2 text-3xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                <span className="bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent">
                  Sultan
                </span>{" "}
                <span className="text-amber-50">Ocakbaşı</span>
              </h1>
            </div>
            <DemoLangSwitch lang={lang} setLang={setLang} tone="dark" />
          </div>

          <div className="mt-8 max-w-2xl">
            <p className="text-2xl sm:text-3xl font-semibold leading-snug" style={{ fontFamily: "Georgia, serif" }}>
              {tx(COPY.hero)}
            </p>
            <p className="mt-3 text-sm sm:text-base text-amber-100/60 leading-relaxed">{tx(COPY.sub)}</p>
          </div>

          {/* Meta + CTA */}
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <ReserveButton lang={lang} message={`Hallo Sultan Ocakbaşı, ich möchte einen Tisch reservieren.`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm transition-all hover:scale-105 shadow-lg shadow-amber-500/30" />
            <a href={COMPANY.instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 font-semibold text-sm transition-colors" data-testid="demo-instagram">
              <Instagram className="h-4 w-4" />
              @{COMPANY.instagramHandle}
            </a>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-300 text-xs font-semibold border border-emerald-500/30">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              {tx(COPY.open)}
            </div>
          </div>

          {/* Rating bar */}
          <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-amber-100/70 pt-8 border-t border-amber-500/20">
            <div className="inline-flex items-center gap-1">
              {[1,2,3,4,5].map((s) => <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
              <span className="ml-1.5 font-bold text-amber-50">4.9</span>
              <span className="text-xs">· 1.247 Bewertungen</span>
            </div>
            <div className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-amber-400" /> Marienburger Str. 5, 45665</div>
            <div className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-400" /> 17:00–23:00</div>
          </div>
        </div>
      </header>

      {/* CATEGORY TABS — sticky */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0807]/85 border-b border-amber-500/15">
        <div className="container mx-auto px-5 sm:px-8 py-3 flex gap-2 overflow-x-auto scrollbar-hide" data-testid="demo-turkish-tabs">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase transition-all ${
                activeCat === c.id
                  ? "bg-gradient-to-b from-amber-400 to-amber-600 text-zinc-950 shadow-lg shadow-amber-500/30"
                  : "text-amber-200/60 hover:text-amber-200 border border-amber-500/20"
              }`}
              data-testid={`demo-tab-${c.id}`}
            >
              {tx(c.label)}
            </button>
          ))}
        </div>
      </nav>

      {/* MENU */}
      <main className="container mx-auto px-5 sm:px-8 py-10 pb-32 max-w-3xl">
        <div className="space-y-5">
          {items.map((it, i) => (
            <article
              key={i}
              className="group relative flex gap-4 p-3 sm:p-4 rounded-2xl border border-amber-500/15 bg-gradient-to-r from-amber-950/10 via-transparent to-transparent hover:border-amber-500/40 hover:from-amber-950/30 transition-all duration-300"
              data-testid={`demo-item-${i}`}
            >
              <img src={it.img} alt={tx(it.name)} className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover ring-1 ring-amber-500/20 shrink-0" loading="lazy" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-lg sm:text-xl leading-tight" style={{ fontFamily: "Georgia, serif" }}>{tx(it.name)}</h3>
                    {it.badge && (
                      <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        <Sparkles className="h-2.5 w-2.5" /> {tx(it.badge)}
                      </span>
                    )}
                  </div>
                  <div className="text-amber-400 font-bold text-base sm:text-lg whitespace-nowrap" style={{ fontFamily: "Georgia, serif" }}>{it.price}</div>
                </div>
                <p className="mt-1.5 text-xs sm:text-sm text-amber-100/55 leading-relaxed">{tx(it.desc)}</p>
              </div>
            </article>
          ))}
        </div>

        {/* Instagram strip */}
        <section className="mt-16 p-6 rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-950/30 to-transparent text-center">
          <Instagram className="h-6 w-6 mx-auto text-amber-400" />
          <div className="mt-2 text-xs uppercase tracking-[0.3em] text-amber-300/70 font-semibold">Instagram</div>
          <h3 className="mt-1 text-xl font-bold" style={{ fontFamily: "Georgia, serif" }}>{tx(COPY.insta)}</h3>
          <a href={COMPANY.instagramUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm transition-all hover:scale-105">
            @{COMPANY.instagramHandle} →
          </a>
        </section>
      </main>

      <StickyActions
        lang={lang}
        onWaiter={callWaiter}
        onBill={requestBill}
        theme={{ waiterCls: "bg-amber-500 text-zinc-950 hover:bg-amber-400", billCls: "bg-zinc-900 text-amber-200 border border-amber-500/30 hover:bg-amber-500/10" }}
      />

      <DemoFooterCTA lang={lang} tone="dark" />
    </div>
  );
}
